import { agentShortName, LISTENING_NOTE_MAX_CHARS, type Agent, type MutableEncryptedBlob } from "@baton/contracts";
import { useEffect, useState } from "react";
import { buildAgentCommands, cleanupCommand, type AgentClient } from "./agent-commands.js";
import { ApiError, ApiErrorCodes, type DashboardApi } from "./api.js";
import { Command, CopyField } from "./components/command.js";
import { Modal } from "./components/modal.js";
import type { CryptoWorkerClient } from "./crypto-client.js";

export interface ConnectAgent {
  id: string;
  handle: string;
  role: string | null;
  color: string;
  /** The stored listening note envelope, when the API reported one; its revision is the row revision. */
  listeningNote?: MutableEncryptedBlob | null | undefined;
  /** Row revision (0 when never set); unlike the envelope it survives a clear. */
  listeningNoteRevision?: number | undefined;
}

/**
 * Everything a member needs to make Claude Code or Codex act as ONE agent, or to talk to it
 * from here instead. Commands carry opaque ids only; the dashboard never runs them. Only the
 * viewer's own agents reach this sheet, so it also edits the agent's listening message.
 */
/** The revision the next write must exceed: the row revision when the API reports it, else the envelope's, else 0. */
function noteRevision(agent: { listeningNote?: MutableEncryptedBlob | null | undefined; listeningNoteRevision?: number | undefined }): number {
  return agent.listeningNoteRevision ?? agent.listeningNote?.revision ?? 0;
}

export function AgentConnectSheet(props: {
  open: boolean;
  onClose(): void;
  agent: ConnectAgent | null;
  /** The signed-in member's handle: only your own agents can be connected, so this is the owner. */
  ownerHandle: string;
  apiUrl: string;
  projectName: string | null;
  api: DashboardApi;
  worker: CryptoWorkerClient;
  onOpenChat(agent: ConnectAgent): void;
  canChat: boolean;
  /** The API's view of the agent after its listening note changed; the owner refreshes its lists. */
  onListeningNoteSaved?: ((agent: Agent) => void) | undefined;
}) {
  const [client, setClient] = useState<AgentClient>("claude");
  const agent = props.agent;
  return <Modal open={props.open && agent !== null} onClose={props.onClose} title={agent ? `Connect ${agentShortName(agent, props.ownerHandle)}` : "Connect"} size="wide">
    {agent && <div className="connect-sheet" data-testid="agent-connect-sheet">
      <p className="connect-sheet-identity">
        <span className="connect-sheet-swatch" style={{ background: agent.color }} aria-hidden="true" />
        <strong>{agentShortName(agent, props.ownerHandle)}</strong>
        <span className="muted">@{props.ownerHandle}</span>
        <span className="flow-chip flow-chip-accent">{agent.role ?? "general"}</span>
      </p>
      <CopyField label="Agent UUID" value={agent.id} />
      <div className="connect-paths">
        <section className="connect-path">
          <p className="eyebrow">A · Connect a client on your machine</p>
          <div className="tabs" role="tablist" aria-label="Client">
            <button type="button" role="tab" aria-selected={client === "claude"} className={client === "claude" ? "active" : ""} onClick={() => setClient("claude")}>Claude Code</button>
            <button type="button" role="tab" aria-selected={client === "codex"} className={client === "codex" ? "active" : ""} onClick={() => setClient("codex")}>Codex</button>
          </div>
          <p className="muted compact">Run these steps on your own computer, in PowerShell or Terminal. Have {client === "claude" ? "Claude Code" : "Codex CLI"} installed and signed in before opening the agent.</p>
          <ol className="connect-steps">
            {buildAgentCommands({ apiUrl: props.apiUrl, agent, client, projectName: props.projectName, ownerHandle: props.ownerHandle }).filter((step) => !step.optional).map((step, index) => <li key={step.id}>
              <p className="connect-step-title">
                <span className="connect-step-index">{index + 1}</span>
                {step.title}
                <span className={`flow-chip connect-scope-${step.scope}`}>{step.scope === "machine" ? "once per machine" : step.scope === "agent" ? "once per agent" : "every session"}</span>
              </p>
              {step.link && <p className="compact"><a href={step.link.href} target="_blank" rel="noopener noreferrer">{step.link.label}</a></p>}
              <Command label={step.title} value={step.command} note={step.note} kind={step.kind} />
            </li>)}
          </ol>
          <details className="connect-optional">
            <summary>Optional: manual setup with plain claude or codex, and headless readiness</summary>
            {buildAgentCommands({ apiUrl: props.apiUrl, agent, client, projectName: props.projectName, ownerHandle: props.ownerHandle }).filter((step) => step.optional).map((step) => <Command key={step.id} label={step.title} value={step.command} note={step.note} kind={step.kind} />)}
          </details>
          <p className="muted compact">
            Had the older machine-wide server installed? Remove it once so its tools are not duplicated: <code>{cleanupCommand(client)}</code>.
            Nothing here embeds a key or token, and the dashboard never configures your machine or starts a client.
          </p>
        </section>
        <section className="connect-path connect-path-chat">
          <p className="eyebrow">B · Talk to it from here</p>
          <p>Open a chat with this agent. Your messages are encrypted in this page's crypto worker and delivered when the agent's client is listening.</p>
          <button type="button" className="primary" disabled={!props.canChat} onClick={() => props.onOpenChat(agent)}>Open a chat</button>
          {!props.canChat && <p className="muted compact">This is your own general agent, the one the dashboard already speaks as.</p>}
        </section>
        <ListeningNoteEditor key={agent.id} agent={agent} api={props.api} worker={props.worker} onSaved={props.onListeningNoteSaved} />
      </div>
    </div>}
  </Modal>;
}

/**
 * The listening message: encrypted here with the team key as a mutable field, sent with the
 * next revision. The relay decrypts it only to answer a Telegram sender while this agent is
 * offline; it is never a Baton message.
 */
function ListeningNoteEditor(props: {
  agent: ConnectAgent;
  api: DashboardApi;
  worker: CryptoWorkerClient;
  onSaved?: ((agent: Agent) => void) | undefined;
}) {
  const [text, setText] = useState("");
  /** What the row currently holds, decrypted; null when the agent has no note. */
  const [stored, setStored] = useState<string | null>(null);
  const [revision, setRevision] = useState(noteRevision(props.agent));
  const [state, setState] = useState<"loading" | "ready" | "saving">("loading");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const envelope = props.agent.listeningNote ?? null;
    setRevision(noteRevision(props.agent)); setError(null); setNotice(null);
    if (!envelope) { setText(""); setStored(null); setState("ready"); return; }
    setState("loading");
    props.worker.call<string>("decryptListeningNote", { agentId: props.agent.id, ciphertext: envelope })
      .then((value) => { if (!cancelled) { setText(value); setStored(value); setState("ready"); } })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof Error ? caught.message : "Could not decrypt the listening message");
        setState("ready");
      });
    return () => { cancelled = true; };
  }, [props.agent.id, props.agent.listeningNote, props.agent.listeningNoteRevision, props.worker]);

  function describeFailure(caught: unknown, fallback: string): string {
    if (caught instanceof ApiError && (caught.is(ApiErrorCodes.listeningNoteConflict) || caught.status === 409)) return "Changed elsewhere, reload";
    return caught instanceof Error ? caught.message : fallback;
  }

  async function save() {
    const trimmed = text.trim();
    if (!trimmed) { await clear(); return; }
    setState("saving"); setError(null); setNotice(null);
    try {
      const next = revision + 1;
      const ciphertext = await props.worker.call<MutableEncryptedBlob>("encryptListeningNote", { agentId: props.agent.id, revision: next, text: trimmed });
      const updated = await props.api.setListeningNote(props.agent.id, { ciphertext, revision: next });
      setRevision(updated.listeningNoteRevision ?? updated.listeningNote?.revision ?? next); setStored(trimmed); setText(trimmed); setNotice("Saved.");
      props.onSaved?.(updated);
    } catch (caught) {
      setError(describeFailure(caught, "Could not save the listening message"));
    } finally {
      setState("ready");
    }
  }

  async function clear() {
    setState("saving"); setError(null); setNotice(null);
    try {
      const next = revision + 1;
      const updated = await props.api.setListeningNote(props.agent.id, { ciphertext: null, revision: next });
      setRevision(updated.listeningNoteRevision ?? next); setStored(null); setText(""); setNotice("Cleared.");
      props.onSaved?.(updated);
    } catch (caught) {
      setError(describeFailure(caught, "Could not clear the listening message"));
    } finally {
      setState("ready");
    }
  }

  const busy = state !== "ready";
  const unchanged = text.trim() === (stored ?? "");
  return <section className="connect-path connect-path-note" data-testid="listening-note">
    <p className="eyebrow">C · Listening message</p>
    <p>When this agent is offline, the Telegram relay answers whoever writes to it with this message. It is encrypted with the team key; the server reads it only while relaying, and it never becomes a Baton message.</p>
    <label>Listening message
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={3}
        maxLength={LISTENING_NOTE_MAX_CHARS}
        disabled={busy}
        placeholder="Back on Monday; @nitant/general can help meanwhile."
      />
    </label>
    <p className="muted compact">{text.length}/{LISTENING_NOTE_MAX_CHARS} · {state === "loading" ? "decrypting…" : stored === null ? "not set" : "set"}</p>
    {error && <p className="error">{error}</p>}
    {notice && <p className="muted compact" role="status">{notice}</p>}
    <div className="connect-note-actions">
      <button type="button" className="primary" disabled={busy || !text.trim() || unchanged} onClick={() => void save()}>{state === "saving" ? "Saving…" : "Save"}</button>
      <button type="button" className="secondary" disabled={busy || stored === null} onClick={() => void clear()}>Clear</button>
    </div>
  </section>;
}
